import { ColTypeDef } from 'ag-grid-community';
import { CustomCellEditorProps } from 'ag-grid-react';
import { NumberMetadata } from 'apiConnector/secondary-market/batch/base';
import { useMinMaxText } from 'components/common-form/hooks/useMinMaxText';
import { NumberAcceleratorCellEditor } from 'components/shared/Blotter/Grid/CellRenderers/number-accelerator-cell-editor';
import { useNumberColumnType as useBaseNumberColumnType } from 'components/shared/Blotter/Grid/Hooks/useNumberColumnType';
import { WithUnit } from 'components/shared/WithUnit/WithUnit';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CellRendererAsEditorWrapper } from 'routes/secondary-market-batch/grid/components/cell-render-as-editor-wrapper';
import {
  asSecondaryCellEditorProps,
  SecondaryCellEditorProps,
  SecondaryCellProps,
  SecondaryCellRendererProps,
} from 'routes/secondary-market-batch/grid/types';
import { RowData } from 'state/workspaces/secondary-market/batch/model';

const CellEditor = (props: SecondaryCellProps<number | undefined>) => {
  const { formatMessage } = useIntl();
  const editorProps = asSecondaryCellEditorProps<number | undefined>(props);

  const onValueChanged = useCallback(
    (v: number | undefined | null) => {
      editorProps.onValueChange?.(v);
    },
    [editorProps],
  );

  const metadata: NumberMetadata | undefined = props.colDef.context.metadataId
    ? (props.data?.metadata?.[props.colDef.context.metadataId] as NumberMetadata)
    : undefined;

  const unit = useMemo(() => {
    return metadata?.unit
      ? formatMessage(
          { id: `secondaryMarket.batch.number.unit` },
          { quantity: metadata?.unitQuantity, unit: metadata?.unit },
        )
      : undefined;
  }, [metadata, formatMessage]);

  const placeholder = useMinMaxText({ min: metadata?.min, max: metadata?.max });

  return (
    <WithUnit className="d-flex justify-content-between w-100" unit={unit} units={unit ? [unit] : undefined}>
      <div className="flex-grow-1">
        <NumberAcceleratorCellEditor
          {...(props as CustomCellEditorProps<RowData, number | undefined>)}
          onValueChange={onValueChanged}
          placeholder={placeholder}
          readOnly={props.type === 'renderer'}
          initialValue={props.type === 'renderer' ? props.value : editorProps.initialValue}
        />
      </div>
    </WithUnit>
  );
};
export function useNumberColumnType() {
  const numberColumnType = useBaseNumberColumnType<RowData>(4);

  return useMemo(
    () =>
      ({
        ...numberColumnType,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'notContains'],
        },
        cellEditor: (p: SecondaryCellEditorProps<number | undefined>) => <CellEditor {...p} type="editor" />,
        cellRenderer: (p: SecondaryCellRendererProps<number | undefined>) => (
          <CellRendererAsEditorWrapper {...p} render={CellEditor} />
        ),
      }) as ColTypeDef<RowData, RowData[keyof RowData]>,
    [numberColumnType],
  );
}
